import { MobileLayout } from '@healthhub/react-ui';
import type { PropsWithChildren } from 'react';

import { i18n } from 'i18n';

import { PageType } from '@shared/constants/constants';
import { useLayoutViewContext } from '@shared/context';
import useOrigin from '@shared/hooks/useOrigin/useOrigin';
import type { RouteConfig } from '@shared/types/common.types';
import { isDev } from '@shared/utils/environment';

import { RestrictPlatformWrapper } from '../error';
import { CommonLayout, PageFooter, PageHeader, PortalLayout } from '../layout';

export const LayoutWrapper = ({
  children,
  layoutProps,
  pageType,
  availableLanguages,
  isNavHeaderHide,
  serviceCatalogCode,
}: PropsWithChildren<
  Pick<RouteConfig, 'availableLanguages' | 'isNavHeaderHide' | 'layoutProps' | 'pageType' | 'serviceCatalogCode'>
>) => {
  const { isCommonView, isMobileView, isSharedServiceView, isStandardView, capabilityVersion, sourceApp } =
    useLayoutViewContext();

  useOrigin();

  if (pageType === PageType.PUBLIC) {
    if (isCommonView) {
      return <CommonLayout {...layoutProps}>{children}</CommonLayout>;
    }

    if (isMobileView || isSharedServiceView) {
      return (
        <MobileLayout
          {...layoutProps}
          isPublic
          layoutView={{
            isSharedServiceView,
            isNavHeaderHide,
            isStandardView,
            sourceApp,
            capabilityVersion,
          }}
          multiLanguageProps={{ availableLanguages, i18n }}
          pageFooter={<PageFooter />}
          pageHeader={<PageHeader />}
        >
          <RestrictPlatformWrapper serviceCatalogCode={serviceCatalogCode}>{children}</RestrictPlatformWrapper>
        </MobileLayout>
      );
    }

    return (
      <PortalLayout {...layoutProps} isPublic>
        <RestrictPlatformWrapper serviceCatalogCode={serviceCatalogCode}>{children}</RestrictPlatformWrapper>
      </PortalLayout>
    );
  }

  if (isStandardView || isSharedServiceView || isMobileView) {
    return (
      <MobileLayout
        {...layoutProps}
        isPublic={isDev()}
        layoutView={{
          isSharedServiceView,
          isNavHeaderHide,
          isStandardView,
          sourceApp,
          capabilityVersion,
        }}
        multiLanguageProps={{ availableLanguages, i18n }}
        pageFooter={<PageFooter />}
        pageHeader={<PageHeader />}
      >
        <RestrictPlatformWrapper serviceCatalogCode={serviceCatalogCode}>{children}</RestrictPlatformWrapper>
      </MobileLayout>
    );
  }

  return (
    <PortalLayout contentClassName="lg:max-w-5xl" {...layoutProps}>
      <RestrictPlatformWrapper serviceCatalogCode={serviceCatalogCode}>{children}</RestrictPlatformWrapper>
    </PortalLayout>
  );
};
